import {helpers} from 'commerce-sdk-isomorphic'
import {useMutation as useReactQueryMutation} from '@tanstack/react-query'
import {useConfig} from '@salesforce/commerce-sdk-react'
import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext'
import {
    generateCustomEndpointOptions,
    handleInvalidToken
} from '@salesforce/commerce-sdk-react/hooks/helpers'

/**
 * A hook for SCAPI custom endpoint mutations.
 * Based on useCustomMutation from @salesforce/commerce-sdk-react,
 * improved to handle `parameters` and `body` passed as mutation variables
 * when calling `mutate` or `mutateAsync`
 *
 * Besides calling custom endpoint, this hook does a few things for better DX.
 * 1. inject access token
 * 2. merge SCAPI client configurations from the CommerceApiProvider
 * @param {import("@salesforce/commerce-sdk-react").OptionalCustomEndpointClientConfig} apiOptions - Options passed through to commerce-sdk-isomorphic
 * @param {import("@tanstack/react-query").UseMutationOptions<unknown, unknown, import("@salesforce/commerce-sdk-react").TMutationVariables>} mutationOptions - Options passed through to @tanstack/react-query
 * @returns {import("@tanstack/react-query").UseMutationResult<unknown, unknown, import("@salesforce/commerce-sdk-react").TMutationVariables, unknown>} A TanStack Query mutation hook with data from the custom API endpoint.
 */
const useImprovedCustomMutation = (apiOptions, mutationOptions) => {
    const auth = useAuthContext()
    const globalConfig = useConfig()
    const logger = globalConfig.logger || console

    /**
     * @returns {function(import("@salesforce/commerce-sdk-react").TMutationVariables): Promise}
     */
    const createMutationFnWithAuth = () => {
        return (
            /**
             * @param {import("@salesforce/commerce-sdk-react").TMutationVariables} args
             * @returns {Promise}
             */
            async (args) => {
                const {access_token} = await auth.ready()
                const mergedOptions = {
                    ...apiOptions,
                    options: {
                        ...apiOptions?.options,
                        body: args?.body || apiOptions?.options?.body,
                        parameters: {
                            ...apiOptions?.options?.parameters,
                            ...args?.parameters
                        }
                    }
                }
                return await helpers
                    .callCustomEndpoint(
                        generateCustomEndpointOptions(
                            mergedOptions,
                            globalConfig,
                            access_token,
                            args
                        )
                    )
                    .catch(async (error) => {
                        const {access_token} = await handleInvalidToken(error, auth, logger)
                        return await helpers.callCustomEndpoint(
                            generateCustomEndpointOptions(
                                mergedOptions,
                                globalConfig,
                                access_token,
                                args
                            )
                        )
                    })
            }
        )
    }

    return useReactQueryMutation(createMutationFnWithAuth(), mutationOptions)
}

export default useImprovedCustomMutation
